import React, { useState } from 'react';
import styled from 'styled-components';
import SLLogo from './assets/slLogo';

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  margin-bottom: 8px;
  max-width: 60%;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const MessageRow = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 4px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.isUser ? '#4a6fa5' : '#e0e0e0'};
  margin-right: ${props => props.isUser ? '0' : '8px'};
  margin-left: ${props => props.isUser ? '8px' : '0'};
  display: flex;
  align-items: center;
  justify-content: center;
  order: ${props => props.isUser ? '1' : '0'};
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #4a6fa5;
  font-size: 14px;
`;

const MessageBubble = styled.div`
  background-color: ${props => props.isUser ? '#4a6fa5' : '#ffffff'};
  color: ${props => props.isUser ? '#ffffff' : '#333333'};
  border-radius: ${props => props.isUser ? '18px 18px 0 18px' : '18px 18px 18px 0'};
  padding: 12px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
  border: ${props => props.isUser ? 'none' : '1px solid #e0e0e0'};
`;

const Timestamp = styled.span`
  font-size: 0.7rem;
  color: #666;
  margin-top: 4px;
  margin-left: ${props => props.isUser ? '0' : '40px'};
  margin-right: ${props => props.isUser ? '40px' : '0'};
`;

const FeedbackContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  margin-left: ${props => props.isUser ? '0' : '40px'};
`;

const FeedbackButton = styled.button`
  background: ${props => props.selected 
    ? (props.thumbsUp 
      ? 'linear-gradient(to bottom, #4CD964, #34C759)' 
      : 'linear-gradient(to bottom, #FF3B30, #FF2D55)')
    : 'transparent'};
  color: ${props => props.selected ? 'white' : '#8E8E93'};
  border: ${props => props.selected ? 'none' : '1px solid #DEDEDE'};
  font-size: 0.9rem;
  cursor: pointer;
  margin-right: 12px;
  padding: 6px 10px;
  border-radius: 20px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  box-shadow: ${props => props.selected ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'};
  
  &:hover {
    background: ${props => props.thumbsUp 
      ? 'linear-gradient(to bottom, #4CD964, #34C759)' 
      : 'linear-gradient(to bottom, #FF3B30, #FF2D55)'};
    color: white;
    border-color: transparent;
  }
  
  span {
    margin-left: 4px;
    font-weight: 500;
  }
`;

const FeedbackFormContainer = styled.div`
  display: ${props => props.isVisible ? 'flex' : 'none'};
  flex-direction: column;
  margin-top: 12px;
  margin-left: 40px;
  max-width: 300px;
  background: #F8F8F8;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
`;

const FeedbackTextarea = styled.textarea`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #E5E5EA;
  resize: vertical;
  min-height: 60px;
  margin-bottom: 10px;
  font-family: inherit;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #4a6fa5;
    box-shadow: 0 0 0 1px rgba(74, 111, 165, 0.2);
  }
`;

const FeedbackSubmit = styled.button`
  background: linear-gradient(to right, #4a6fa5, #5D82B5);
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  align-self: flex-end;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(74, 111, 165, 0.2);
  
  &:hover {
    background: linear-gradient(to right, #3d5d8a, #4F73A5);
    box-shadow: 0 2px 6px rgba(74, 111, 165, 0.3);
  }
`;

const FeedbackTag = styled.div`
  background: ${props => props.thumbsUp ? '#E8F5E9' : '#FFEBEE'};
  color: ${props => props.thumbsUp ? '#2E7D32' : '#C62828'};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  
  span {
    margin-right: 4px;
  }
`;

const formatTime = (date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const ChatMessage = ({ message, onFeedback }) => {
  const isUser = message.sender === 'user';
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  
  const handleFeedback = (thumbsUp) => {
    if (thumbsUp === false) {
      setShowFeedbackForm(true);
    } else {
      onFeedback(thumbsUp, '');
    }
    setFeedbackGiven(thumbsUp);
  };
  
  const handleSubmitFeedback = () => {
    onFeedback(false, feedbackText);
    setShowFeedbackForm(false);
  };
  
  return (
    <MessageContainer isUser={isUser}>
      <MessageRow isUser={isUser}>
        <Avatar isUser={isUser}>
          {isUser ? (
            <UserAvatar>U</UserAvatar>
          ) : (
            <SLLogo width={20} height={20} />
          )}
        </Avatar>
        <MessageBubble isUser={isUser}>
          {message.text}
        </MessageBubble>
      </MessageRow>
      
      <Timestamp isUser={isUser}>
        {formatTime(message.timestamp)}
        {feedbackGiven !== null && (
          <FeedbackTag thumbsUp={feedbackGiven}>
            <span>{feedbackGiven ? '👍' : '👎'}</span>
            {feedbackGiven ? 'Helpful' : 'Not helpful'}
          </FeedbackTag>
        )}
      </Timestamp>
      
      {!isUser && (
        <>
          <FeedbackContainer>
            <FeedbackButton 
              onClick={() => handleFeedback(true)}
              thumbsUp
              selected={feedbackGiven === true}
            >
              👍 <span>Helpful</span>
            </FeedbackButton>
            <FeedbackButton 
              onClick={() => handleFeedback(false)}
              thumbsUp={false}
              selected={feedbackGiven === false}
            >
              👎 <span>Not Helpful</span>
            </FeedbackButton>
          </FeedbackContainer>
          
          <FeedbackFormContainer isVisible={showFeedbackForm}>
            <FeedbackTextarea
              placeholder="How can we improve this response?"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
            <FeedbackSubmit onClick={handleSubmitFeedback}>
              Submit
            </FeedbackSubmit>
          </FeedbackFormContainer>
        </>
      )}
    </MessageContainer>
  );
};

export default ChatMessage;